import React, { useState, useRef, useCallback, useEffect } from "react";
import { ArrowsLeftRight } from "@phosphor-icons/react";

/**
 * BeforeAfterSlider — perspective-stable comparator.
 *
 * Both images are rendered at the SAME absolute size (full container) so
 * their geometry never changes. We reveal/hide the "before" image using a
 * CSS clip-path on the image itself, which is a free GPU operation that
 * does NOT scale or distort the underlying pixels.
 */
export default function BeforeAfterSlider({ beforeSrc, afterSrc, height = "h-[60vh]", rotate = 0, fit = "cover", autoPlay = false, objectPosition = "center" }) {
  // iter v32 r48 — User M1446: «la lineetta verde con le due freccette
  // bianche non segue il taglio delle due immagini, devono essere una
  // sopra l'altra». BUG MATEMATICO: il vecchio codice usava
  // `clipPath: inset(0 ${pos}% 0 0)` con la linea a `left: pos%`. Il
  // clipPath taglia (100 - pos)% da destra ⇒ la giunzione cadeva a
  // (100 - pos)% mentre la linea era a pos%. Allineate solo al 50%.
  // Nuovo modello: pos rappresenta la POSIZIONE ORIZZONTALE della linea
  // (e della giunzione). Convenzione: slider all'estrema destra → tutto
  // PRIMA, all'estrema sinistra → tutto DOPO. AutoPlay parte da 100
  // (PRIMA piena) e sweepa verso 25 (75% DOPO svelato da sinistra).
  const [pos, setPos] = useState(autoPlay ? 100 : 50);
  const [hasInteracted, setHasInteracted] = useState(false);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
    setHasInteracted(true);
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    updateFromClientX(e.clientX ?? e.touches?.[0]?.clientX);
  };

  // Global listeners so the drag keeps working even if the cursor leaves the box
  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.clientX ?? e.touches?.[0]?.clientX;
      if (x != null) updateFromClientX(x);
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [updateFromClientX]);

  // A) HOLD 1s → pos=100 → si vede tutto il PRIMA
  // B) SWEEP 3s → 100 → 25 (rivela il DOPO da sinistra verso destra)
  // C) LOOP continuo 80 ↔ 25 con periodo 10s
  useEffect(() => {
    if (!autoPlay || hasInteracted) return;
    let raf = 0;
    const startTime = performance.now();
    const HOLD_MS = 1000;
    const SWEEP_MS = 3000;
    const LOOP_PERIOD = 10000;
    const SWEEP_FROM = 100; // tutto PRIMA
    const SWEEP_TO = 25;    // 75% DOPO svelato
    const LOOP_MIN_POS = 25; // 75% DOPO
    const LOOP_MAX_POS = 80; // 20% DOPO
    const tick = (t) => {
      const elapsed = t - startTime;
      let next;
      if (elapsed < HOLD_MS) {
        next = SWEEP_FROM;
      } else if (elapsed < HOLD_MS + SWEEP_MS) {
        const p = (elapsed - HOLD_MS) / SWEEP_MS;
        const eased = p < 0.5
          ? 4 * p * p * p
          : 1 - Math.pow(-2 * p + 2, 3) / 2;
        next = SWEEP_FROM + eased * (SWEEP_TO - SWEEP_FROM);
      } else {
        const phase = ((elapsed - HOLD_MS - SWEEP_MS) % LOOP_PERIOD) / LOOP_PERIOD;
        const eased = 0.5 - 0.5 * Math.cos(phase * Math.PI * 2);
        next = LOOP_MAX_POS - eased * (LOOP_MAX_POS - LOOP_MIN_POS);
      }
      setPos(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoPlay, hasInteracted]);

  // iter v32 r48 · CLIP-PATH ALLINEATO ALLA LINEA.
  // PRIMA come layer sopra: visibile da 0% a pos% (a sinistra della linea).
  // DOPO come layer sotto: sempre visibile a destra di pos%.
  //   pos=0   → PRIMA tagliato totalmente → solo DOPO
  //   pos=100 → PRIMA non tagliato         → solo PRIMA
  //   pos=50  → metà sx PRIMA, metà dx DOPO (linea esattamente sul taglio)
  // Adesso linea e taglio coincidono in ogni istante (no più "linea che
  // taglia attraverso il divano").
  const clipPath = `inset(0 ${100 - pos}% 0 0)`;

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${height} overflow-hidden select-none cursor-ew-resize bg-[#1C1C1A]`}
      data-testid="before-after-slider"
      onMouseDown={onPointerDown}
      onTouchStart={onPointerDown}
    >
      {/* Both images rendered at full size — no resizing ever, just clipped */}
      <img
        src={afterSrc}
        alt="Dopo"
        className={`absolute inset-0 w-full h-full object-${fit}`}
        style={{ objectPosition, ...(rotate ? { transform: `rotate(${rotate}deg)` } : {}) }}
        draggable="false"
      />
      <img
        src={beforeSrc}
        alt="Prima"
        className={`absolute inset-0 w-full h-full object-${fit}`}
        style={{ clipPath, WebkitClipPath: clipPath, objectPosition, ...(rotate ? { transform: `rotate(${rotate}deg)` } : {}) }}
        draggable="false"
      />

      {/* Labels */}
      <span className="absolute top-4 left-4 overline bg-[#FFFFFF]/80 px-3 py-1" data-testid="label-prima">Prima</span>
      <span className="absolute top-4 right-4 overline bg-[#FFFFFF]/80 px-3 py-1" data-testid="label-dopo">Dopo</span>

      {/* Slider line + handle */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-[#7C8B6E] ba-handle pointer-events-none"
        style={{ left: `calc(${pos}% - 1px)` }}
        data-testid="slider-line"
      >
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 w-10 h-10 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center shadow-[0_6px_20px_rgba(0,0,0,0.18)] pointer-events-auto"
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
          data-testid="slider-handle"
        >
          <ArrowsLeftRight size={18} weight="bold" />
        </div>
      </div>
    </div>
  );
}
